import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { secureGet, securePost } from '../../config/api';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { formatAmount } from '../../utils/format';

import Breadcrumbs from '../../components/Breadcrumbs';
import StatusChain, { STATUS_COLORS } from '../../components/StatusChain';
import Input from '../../components/Input';
import { PrimaryButton, SaveCancel } from '../../components/PrimaryButton';
import SnackbarNotification from '../../components/SnackbarNotification';
import LoadingLogo from '../../components/LoadingLogo';

import plusIcon from '../../icons/plusB.svg';
import fileTextIcon from '../../icons/file-textBl.svg';
import linkIcon from '../../icons/linkGr.svg';
import xWIcon from '../../icons/link_off.svg';
import trashIcon from '../../icons/trashW.svg';
import chevronRightIcon from '../../icons/chevron-right.svg';
import repairIcon from '../../icons/repair.svg';

const DeliveryNoteForm = () => {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();
  const fromOrder = location.state?.fromOrder;
  console.log("sfgertg", fromOrder)

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [repos, setRepos] = useState([]);
  const [products, setProducts] = useState([]);
  const [repoId, setRepoId] = useState('');
  const [mappings, setMappings] = useState({}); 
  const [extraLines, setExtraLines] = useState([]); 
  const [isAddPopupOpen, setAddPopupOpen] = useState(false);
  const [newLine, setNewLine] = useState({ productId: '', quantity: '', unitPrice: '', vatRate: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  useEffect(() => {
    if (!fromOrder?.id) {
      history.replace('/orders');
      return;
    }
    const abortController = new AbortController();
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [orderRes, reposRes, productsRes] = await Promise.all([
          secureGet(`/carts/${fromOrder.id}`, showSnackbar, { signal: abortController.signal }),
          secureGet('/repos', showSnackbar, { signal: abortController.signal }),
          secureGet('/products/unpaginated?familyTypes=PRODUCT', showSnackbar, { signal: abortController.signal }),
        ]);
        setOrder(orderRes.data);
        setRepos((reposRes.data || []).map((r) => ({ value: r.id, label: r.name })));
        const productsData = productsRes.data?.content || productsRes.data || [];
        setProducts(productsData.map((p) => ({ value: p.id, reference: p.reference, name: p.name, fullEntity: p })));
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Failed to load delivery note data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromOrder?.id]);

  const findProduct = (productId) => products.find((p) => String(p.value) === String(productId));

  const { totalHT, totalTTC } = useMemo(() => {
    let ht = 0;
    let ttc = 0;
    (order?.details || []).forEach((cd) => {
      const productId = mappings[cd.id];
      if (!productId) return;
      // Mapped from the order: use the price set on the quote's free line, not the catalog price.
      const unitPriceHT = cd.unitPrice != null ? Number(cd.unitPrice) : 0;
      const vatRate = cd.vatRate != null ? Number(cd.vatRate) : 20;
      const unitPriceTTC = unitPriceHT * (1 + vatRate / 100);
      ht += unitPriceHT * cd.quantity;
      ttc += unitPriceTTC * cd.quantity;
    });
    extraLines.forEach((line) => {
      const product = findProduct(line.productId)?.fullEntity;
      const unitPriceHT = line.unitPrice != null ? Number(line.unitPrice) : (product?.priceHT != null ? Number(product.priceHT) : 0);
      const vatRate = line.vatRate != null ? Number(line.vatRate) : (product?.tva != null ? Number(product.tva) : 20);
      const unitPriceTTC = unitPriceHT * (1 + vatRate / 100);
      ht += unitPriceHT * line.quantity;
      ttc += unitPriceTTC * line.quantity;
    });
    return { totalHT: ht, totalTTC: ttc };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappings, order, products, extraLines]);

  const handleMapLine = (cartDetailId, productId) => {
    setMappings((prev) => ({ ...prev, [cartDetailId]: productId }));
  };

  const handleUnmapLine = (cartDetailId) => {
    setMappings((prev) => {
      const next = { ...prev };
      delete next[cartDetailId];
      return next;
    });
  };

  const handleAddExtraLine = () => {
    const { productId, quantity, unitPrice, vatRate } = newLine;
    if (!productId || !quantity || Number(quantity) <= 0) {
      showSnackbar(t('placeholder.empty_fields'), 'error');
      return;
    }
    const isAlreadyMapped = Object.values(mappings).some((mappedId) => String(mappedId) === String(productId));
    if (isAlreadyMapped) {
      showSnackbar(
        "Ce produit existe déjà, vous ne pouvez pas ajouter plus de quantités, vérifiez avec le conseiller de service",
        'error'
      );
      return;
    }
    const existingIndex = extraLines.findIndex((line) => String(line.productId) === String(productId));
    if (existingIndex !== -1) {
      setExtraLines((prev) => prev.map((line, i) => (
        i === existingIndex ? { ...line, quantity: line.quantity + parseInt(quantity, 10) } : line
      )));
    } else {
      setExtraLines((prev) => [...prev, {
        productId,
        quantity: parseInt(quantity, 10),
        unitPrice: unitPrice !== '' ? Number(unitPrice) : undefined,
        vatRate: vatRate !== '' ? Number(vatRate) : undefined,
      }]);
    }
    setNewLine({ productId: '', quantity: '', unitPrice: '', vatRate: '' });
    setAddPopupOpen(false);
  };

  const handleRemoveExtraLine = (index) => {
    setExtraLines((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!repoId) {
      showSnackbar('Veuillez remplir les champs obligatoires : Stock', 'error');
      return;
    }
    const mappedDetails = Object.entries(mappings)
      .filter(([, productId]) => !!productId)
      .map(([cartDetailId, productId]) => ({
        productId: parseInt(productId, 10),
        quantity: order.details.find((cd) => String(cd.id) === String(cartDetailId))?.quantity,
        cartDetailId: parseInt(cartDetailId, 10),
      }));
    const extraDetails = extraLines.map((line) => ({
      productId: parseInt(line.productId, 10),
      quantity: line.quantity,
      unitPrice: line.unitPrice != null ? Number(line.unitPrice) : undefined,
      vatRate: line.vatRate != null ? Number(line.vatRate) : undefined,
    }));
    if (mappedDetails.length === 0 && extraDetails.length === 0) {
      showSnackbar(t('placeholder.empty_fields'), 'error');
      return;
    }
    const payload = {
      cartId: order.id,
      repoId: parseInt(repoId, 10),
      details: [...mappedDetails, ...extraDetails],
    };
    try {
      setSubmitting(true);
      await securePost('/deliveryNotes/add', payload, showSnackbar);
      history.push({ pathname: `/orders/details/${order.id}` });
    } catch (error) {
      console.error('Failed to create delivery note:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !order) return <LoadingLogo />;

  const deliveryNotesCount = Array.isArray(order.deliveryNotes) ? order.deliveryNotes.length : 0;
  // Nothing left to map (everything already delivered or only "réparé" lines remain) and no extra
  // lines added either - there's nothing to submit.
  const mappableRemaining = (order.details || []).filter((cd) => !cd.hasDeliveryNote && !cd.notForProcurement);
  const nothingToSubmit = mappableRemaining.length === 0 && extraLines.length === 0;

  return (
    <div className="h-full flex flex-col">
      <Helmet>
        <title>Drivnest - {t('titles.bl') || 'Bon de livraison'}</title>
      </Helmet>

      <div className="flex-shrink-0 pb-2">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-[#000000]">Créer un bon de livraison</h1>

        <div className="flex items-center gap-3 mb-1">
          <p className="text-[24px] font-semibold">{order.cartNumber}</p>
          <StatusChain
            items={[
              { key: 'bc', label: 'BC', color: STATUS_COLORS.active },
              { key: 'bl', label: 'BL', color: deliveryNotesCount > 0 ? STATUS_COLORS.active : STATUS_COLORS.missing },
            ]}
          />
        </div>
        <p className="text-[20px] font-semibold text-[#38485C] mb-3">{order.client?.name}</p>

        <div className="max-w-[320px]">
          <Input type="dropdown" label="Stock" name="repoId" value={repoId} onChange={setRepoId} options={repos} required />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-[16px] font-medium text-[#ACAFC6]">LISTE DES PRODUITS</h3>
          <PrimaryButton label="Ajouter" icon={plusIcon} onClick={() => setAddPopupOpen(true)} />
        </div>

        <div className="grid grid-cols-[1fr_auto_2fr] items-center gap-4 mb-2 text-[14px] font-semibold">
          <div className="flex items-center gap-2 text-[#4154A2]">
            <img src={fileTextIcon} alt="" className="w-4 h-4" /> Saisi sur le devis
          </div>
          <div className="w-4 h-4" />
          <div className="flex items-center gap-2 text-[#0AD69F]">
            <img src={linkIcon} alt="" className="w-4 h-4" /> Lié par le magasinier
          </div>
        </div>

        <div className="space-y-3">
          {(order.details || []).filter((cd) => !cd.hasDeliveryNote).map((cd) => {
            if (cd.notForProcurement) {
              return (
                <div key={cd.id} className="grid grid-cols-[1fr_auto_2fr] items-center gap-4">
                  <div className="max-h-[72px] flex flex-col justify-center gap-1 bg-white rounded-[12px] px-5 py-3 shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-[#6B78F8] font-semibold text-[16px] truncate min-w-0">{cd.product?.name ?? cd.designation}</p>
                      <img src={repairIcon} alt="À réparer — ne pas acheter" title="À réparer — ne pas acheter" className="w-4 h-4 flex-shrink-0" />
                    </div>
                    <p className="text-[#707070] text-[14px] font-semibold">
                      QUANTITÉ: <span className="text-[#043F88] ml-1">{cd.quantity}</span>
                    </p>
                  </div>
                  <img src={chevronRightIcon} alt="" className="w-4 h-4" />
                  <div className="min-h-[72px] flex items-center justify-center border border-dashed border-[#CDCBF3] bg-[#EDEDED] rounded-[12px] px-5 text-center text-[#707070]/50 text-[14px] font-medium shadow-[0px 6px 26px #626B7D12] pointer-events-none">
                    À Réparer — ne pas acheter
                  </div>
                </div>
              );
            }

            const mappedProductId = mappings[cd.id];
            const mappedProduct = mappedProductId ? findProduct(mappedProductId)?.fullEntity : null;
            return (
              <div key={cd.id} className="grid grid-cols-[1fr_auto_2fr] items-center gap-4">
                <div className="max-h-[72px] flex flex-col justify-center gap-1 bg-white rounded-[12px] px-5 py-3 shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
                  <p className="text-[#043F88] font-semibold text-[16px] truncate">{cd.product?.name ?? cd.designation}</p>
                  <p className="text-[#707070] text-[14px] font-semibold">
                    QUANTITÉ: <span className="text-[#043F88] ml-1">{cd.quantity}</span>
                  </p>
                </div>
                <img src={chevronRightIcon} alt="" className="w-4 h-4" />
                {mappedProduct ? (
                  <div className="min-h-[72px] flex items-center gap-3 bg-[#0AD69E12] border border-[#0AD69F] rounded-[12px] px-4 py-3 shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
                    <div className="flex-1 min-w-0 grid grid-cols-[2fr_1fr_1fr_1fr] items-center gap-2">
                      <div className="min-w-0">
                        <p className="text-[12px] font-semibold text-[#707070]">{mappedProduct.reference}</p>
                        <p className="text-[#6B78F8] font-semibold text-[15px] truncate">{mappedProduct.name}</p>
                      </div>
                      <div className="text-left">
                        <p className="text-[12px] text-[#707070] font-semibold">QUANTITÉ</p>
                        <p className="text-[#043F88] font-semibold text-[16px]">{cd.quantity}</p>
                      </div>
                      <div className="text-left">
                        <p className="text-[12px] text-[#707070] font-semibold">PRIX UN HT</p>
                        <p className="text-[#043F88] font-semibold text-[16px]">{formatAmount(cd.unitPrice)}</p>
                      </div>
                      <div className="text-left">
                        <p className="text-[12px] text-[#707070] font-semibold">PRIX TOTAL</p>
                        <p className="text-[#043F88] font-semibold text-[16px]">
                          {formatAmount(Number(cd.unitPrice || 0) * cd.quantity)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleUnmapLine(cd.id)}
                      className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-[#3872B0] rounded-[12px]"
                      title="Annuler le lien"
                    >
                      <img src={xWIcon} alt="" className="w-4 h-4" style={{ filter: 'brightness(0) invert(1)' }} />
                    </button>
                  </div>
                ) : (
                  <div className="min-h-[72px] flex items-center gap-3 bg-white rounded-[12px] px-4 py-3 shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
                    <div className="flex-1">
                      <Input
                        type="dropdown"
                        label="Rechercher un produit du catalogue..."
                        name={`map-${cd.id}`}
                        value={mappedProductId || ''}
                        onChange={(value) => handleMapLine(cd.id, value)}
                        options={products}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {extraLines.map((line, index) => {
            const product = findProduct(line.productId)?.fullEntity;
            const unitPriceHT = line.unitPrice != null ? Number(line.unitPrice) : (product?.priceHT ?? 0);
            const vatRate = line.vatRate != null ? Number(line.vatRate) : (product?.tva ?? 20);
            return (
              <div key={`extra-${index}`} className="grid grid-cols-[1fr_auto_2fr] items-center gap-4">
                <div className="min-h-[72px] flex items-center justify-center border border-dashed border-[#CDCBF3] bg-[#EDEDED] rounded-[12px] px-5 text-center text-[#707070]/50 text-[14px] font-medium shadow-[0px 6px 26px #626B7D12] pointer-events-none">
                  Aucune ligne sur le devis
                </div>
                <img src={chevronRightIcon} alt="" className="w-4 h-4" />
                <div className="min-h-[72px] flex items-center gap-3 bg-[#0AD69E12] border border-[#0AD69F] rounded-[12px] px-4 py-3 shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
                  <div className="flex-1 min-w-0 grid grid-cols-[2fr_1fr_1fr_1fr_1fr] items-center gap-2">
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold text-[#707070]">{product?.reference}</p>
                      <p className="text-[#6B78F8] font-semibold text-[15px] truncate">{product?.name}</p>
                    </div>
                    <div className="text-left">
                      <p className="text-[12px] text-[#707070] font-semibold">PRIX UN HT</p>
                      <p className="text-[#043F88] text-[16px] font-semibold">{formatAmount(unitPriceHT)}</p>
                    </div>
                    <div className="text-left">
                      <p className="text-[12px] text-[#707070] font-semibold">QUANTITÉ</p>
                      <p className="text-[#043F88] text-[16px] font-semibold">{line.quantity}</p>
                    </div>
                    <div className="text-left">
                      <p className="text-[12px] text-[#707070] font-semibold">TVA</p>
                      <p className="text-[#043F88] text-[16px] font-semibold">{vatRate}%</p>
                    </div>
                    <div className="text-left">
                      <p className="text-[12px] text-[#707070] font-semibold">PRIX TOTAL</p>
                      <p className="text-[#043F88] font-semibold text-[16px]">
                        {formatAmount(unitPriceHT * (1 + vatRate / 100) * line.quantity)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveExtraLine(index)}
                    className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-[#EF466F] rounded-[12px] shadow-[0px 4px 10px #BEBEBE50]"
                  >
                    <img src={trashIcon} alt="" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex-shrink-0 flex justify-between items-center pt-4 ">
        <div className="flex items-center gap-[50px]">
          <p className="text-[16px]">
            <span className="text-[#231E29] font-medium">TOTAL HT: </span>
            <span className="text-[#043F88] font-bold">{formatAmount(totalHT)}</span>
          </p>
          <p className="text-[16px]">
            <span className="text-[#231E29] font-medium">TOTAL TTC: </span>
            <span className="text-[#043F88] font-bold">{formatAmount(totalTTC)}</span>
          </p>
        </div>
        <SaveCancel onSaveClick={handleSubmit} onCancelClick={() => history.goBack()} loading={submitting} disabled={nothingToSubmit} />
      </div>

      {isAddPopupOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
          <div className="w-[750px] bg-[#F9FAFC] rounded-[12px] shadow-[0px_20px_80px_#00000011] p-6">
            <h2 className="text-[24px] font-semibold text-[#38485C] mb-4">Ajouter une ligne</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Input
                type="dropdown"
                label="Produit du catalogue"
                name="newLineProduct"
                value={newLine.productId}
                onChange={(value) => setNewLine((prev) => ({ ...prev, productId: value }))}
                options={products}
                required
              />
              <Input
                type="number"
                label="Quantité"
                name="newLineQuantity"
                value={newLine.quantity}
                onChange={(value) => setNewLine((prev) => ({ ...prev, quantity: value }))}
                required
              />
              <Input
                type="number"
                label="Prix unitaire de vente recommandé"
                name="newLineUnitPrice"
                value={newLine.unitPrice}
                onChange={(value) => setNewLine((prev) => ({ ...prev, unitPrice: value }))}
                required
              />
              <Input
                type="number"
                label="TVA (%)"
                name="newLineVatRate"
                value={newLine.vatRate}
                onChange={(value) => setNewLine((prev) => ({ ...prev, vatRate: value }))}
                required
              />
            </div>
            <div className="flex justify-end">
              <SaveCancel
                onCancelClick={() => { setAddPopupOpen(false); setNewLine({ productId: '', quantity: '', unitPrice: '', vatRate: '' }); }}
                onSaveClick={handleAddExtraLine}
              />
            </div>
          </div>
        </div>
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default DeliveryNoteForm;
